















import {
  Vue,
  Component,
  Prop,
} from "@/shared/vuepack";
import _ from "lodash";
import moment from "moment";
import { GenericGraphPoint } from "@/shared/types";
import { formatCurrency } from "@/shared/currency";
import { GChart } from "vue-google-charts";
import { useThemeStore } from "@/shared/store/theme";

@Component({
  components: {
    GChart
  }
})
export default class GenericLineChart extends Vue {
  themeStore = useThemeStore();
  manualXAxisLabelCutoff = 7;

  @Prop()
  height: number;

  @Prop({ required: true })
  vAxisTitle: string;

  @Prop({ required: true })
  hAxisTitle: string;

  @Prop({ required: false })
  hAxisLabelAsRange: boolean;

  @Prop({ required: true })
  valueLabel: string;

  @Prop({ required: true })
  dateFormatString: string;

  @Prop({ required: true })
  dataSet: GenericGraphPoint[];

  get getChartOptions (): any {
    return {
      fontName: this.themeStore.fontFace,
      annotations: {
        color: this.themeStore.tertiaryColor
      },
      titlePosition: "none",
      colors: [ "#B2BCBB" ],
      tooltip: {
        isHtml: true,
      },
      chartArea: {
        top: 15,
        height: this.hAxisLabelAsRange ? "86%" : "75%",
      },
      hAxis: {
        title: this.hAxisTitle,
        titleTextStyle: {
          bold: true,
          italic: false,
        },
        baselineColor: "black",
        baseline: _.head(this.xAxisTicks),
        format: this.dateFormatString,
        gridlines: {
          color: "transparent",
          minSpacing: 90
        },
        ticks: this.xAxisTicks,
        textPosition: this.hAxisLabelAsRange ? "none" : "out"
      },
      vAxis: {
        title: this.vAxisTitle,
        baseline: _.head(this.yAxisTicks),
        titleTextStyle: {
          bold: true,
          italic: false,
        },
        baselineColor: "black",
        format: "currency",
        gridlines: {
          color: "transparent",
        },
        minorGridlines: {
          color: "transparent",
        },
      },
      height: this.height,
      legend: { position: "none" },
      enableInteractivity: true,
    };
  }

  tooltipFormatter (data: GenericGraphPoint): string {

    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          ${moment(data.date, "DD-MMM-YY").format("MMM DD YYYY")}
                        </div>
                        <div style="margin-bottom: 3px;">
                          ${this.valueLabel}:
                        </div>
                        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                          ${formatCurrency(data.value)}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                        </div>`;

    return tooltipHtml;
  }

  formatChartDataItem (chartPoint: GenericGraphPoint): any {
    return [
      moment(chartPoint.date, "DD-MMM-YY").toDate(),
      chartPoint.value,
      `stroke-color: ${this.themeStore.tertiaryColor}; fill-opacity: 0`,
      this.tooltipFormatter(chartPoint),
    ];
  }

  get xAxisTicks () {
    const distinctDates = _.uniq(this.dataSet.map(x => x.date));

    if (distinctDates.length) {
      const firstDate = _.head(distinctDates);
      const lastDate = _.last(distinctDates);
      const timePeriodInDays = moment(lastDate).diff(firstDate, "days");
      const interval = timePeriodInDays / 4;

      return [
        moment(firstDate, "DD-MMM-YY").toDate(),
        moment(moment(firstDate).add(interval, "days"), "DD-MMM-YY").toDate(),
        moment(moment(firstDate).add((interval * 2), "days"), "DD-MMM-YY").toDate(),
        moment(moment(firstDate).add((interval * 3), "days"), "DD-MMM-YY").toDate(),
        moment(lastDate, "DD-MMM-YY").toDate(),
      ];
    }
    return [];
  }

  get displayDateRangeLabel (): boolean {
    return this.hAxisLabelAsRange && !!this.xAxisTicks.length;
  }

  get firstDateInSeries (): string {
    const firstDate = _.head(this.xAxisTicks);

    return firstDate ? moment(firstDate).format("MM/DD/YYYY") : "";
  }

  get lastDateInSeries (): string {
    const lastDate = _.last(this.xAxisTicks);

    return lastDate ? moment(lastDate).format("MM/DD/YYYY") : "";
  }

  get yAxisTicks () {
    if (this.dataSet.length) {
      const maxValue = _.maxBy(this.dataSet, "value").value;
      const minValue = _.minBy(this.dataSet, "value").value;
      const differenceInValues = maxValue - minValue;
      const interval = differenceInValues / 3;

      return [
        Math.floor(minValue / 10) * 10,
        Math.floor((minValue + interval) / 10) * 10,
        Math.ceil((minValue + (interval * 2)) / 10) * 10,
        Math.ceil(maxValue / 10) * 10,
      ];
    }
    return [];
  }

  get formattedChartData () {
    const formattedChartPoints = this.dataSet.map(this.formatChartDataItem);

    return [
      [
        this.hAxisTitle,
        this.vAxisTitle,
        { type: "string", role: "style" },
        {
          type: "string", role: "tooltip", p: {
            html: true
          }
        },
      ],
      ...formattedChartPoints
    ];
  }
}
